import { Autocomplete, Box, Grid, TextField } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect } from "react";
import ContactDetails from "src/careflow/care-coordination/patient/contact-detail/ContactDetails";
import AddAddressLoading from "src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddAddressLoading";
import { EditableCard } from "src/careflow/common/EditableCard";
import { ValueSetWrapper } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet";
import { useValueSet } from "src/careflow/ph-core/fhir-client/fhir-react";
import { Address, ContactPoint, OrganizationContact } from "src/careflow/ph-core/fhir-client/resources";
import CustomModal, { CustomModalBasicProps } from "src/components/CustomModal";
import useObjectState from "src/hooks/useObjectState";
import * as Yup from 'yup';

export interface ContactModalProps extends CustomModalBasicProps {
  contact: OrganizationContact | null;
  updateOrganization: Function;
  translate: any;
}

interface ContactState{
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  telecom: ContactPoint[] | null;
  address: Address | null;
  purpose: any;
  openAddAddress: boolean;
};

interface ErrorFieldState {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;

}

const ContactModal = ({
  open,
  contact,
  updateOrganization,
  translate,
  onClose,
  ...modalProps
}: ContactModalProps) => {

  const [valueSet] = useValueSet('contactentity-type',
    {map: ValueSetWrapper, autofetch: !!open}
  );

  const EventSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('First Name is required')
      .matches(/^[a-zA-ZÑñ\s]+$/, 'First Name cannot contain numbers or symbols')
      .max(25, 'First Name must be a maximum of 25 characters'),
    lastName: Yup.string()
      .required('Last Name is required')
      .matches(/^[a-zA-ZÑñ\s-]+$/, 'Last Name cannot contain numbers or symbols')
      .max(25, 'Last Name must be a maximum of 25 characters'),
    middleName: Yup.string()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .matches(/^[a-zA-ZÑñ\s]+$/, 'Middle Name cannot contain numbers or symbols')
      .max(25, 'Middle Name must be a maximum of 25 characters'),
  });

  const validateFields = async () => {
    const validateFieldsSchema = await EventSchema.validate({
      firstName: firstName?.trim(),
      middleName: middleName?.trim(),
      lastName: lastName?.trim(),
    }, { abortEarly: false }).catch((err) => err);

    if (validateFieldsSchema.inner) {
      const errors = validateFieldsSchema.inner.reduce((acc, error) => {
        acc[error.path] = error.message;
        return acc;
      }, {});

      updateErrorState({
        firstName: errors.firstName,
        lastName: errors.lastName,
        middleName: errors.middleName
      });

      return false;
    }

    updateErrorState({
      firstName: null,
      lastName: null,
      middleName: null
    });

    return true;
  };

  const [{
    id,
    firstName,
    lastName,
    middleName,
    telecom,
    address,
    purpose,
    openAddAddress
  }, updateState] = useObjectState<ContactState>({
    id: null,
    firstName: null,
    lastName: null,
    middleName: null,
    telecom: null,
    address: null,
    purpose: null,
    openAddAddress: false
  });

  const [errors, updateErrorState] = useObjectState<ErrorFieldState>({
    firstName: null,
    lastName: null,
    middleName: null
  });

  useEffect(() => {
    if(!open) {
      updateErrorState({
        firstName: null,
        lastName: null,
        middleName: null
      });
      return;
    };

    updateState({
      id: contact?.id ?? null,
      firstName: contact?.name?.given?.[0] ?? null,
      lastName: contact?.name?.family ?? null,
      middleName: contact?.name?.given?.[1] ?? null,
      telecom: contact?.telecom ?? null,
      address: contact?.address ?? null,
      purpose: contact?.purpose ?  { ...contact?.purpose?.coding?.[0] } : null
    })
  },[contact, open]);

  const handleSave = async () => {

    const validate = await validateFields();

    if(!validate) return;
    
    const mapContact = {
      id,
      name: {
        family: lastName,
        given: [firstName, ...(!isEmpty(middleName) ? [middleName] : [])],
      },
      ...(!isEmpty(address) && { address: address}),
      ...(!isEmpty(telecom) && { telecom: telecom }),
      ...(!isEmpty(purpose) && { purpose: {
          coding: [purpose],
          text: purpose?.display
        }
      })
    }
    await updateOrganization(mapContact);
  };

  return (
    <>
      <CustomModal
        open={open}
        title={translate('details.general.contact')}
        breadcrumbs={[""]}
        isLoading={false}
        onSave={handleSave}
        onCancel={onClose as Function}
        onClose={onClose}
        {...modalProps}
      >
        <Box sx={{ width: '100%' }}>
          <Grid container sx={{ width: '100%' }} spacing={2}>
            <EditableCard
              title={translate('details.general.personalDetails')}
              fullWidth={true}
              showEditButton={false}
            >
              <Grid container spacing={1} mt={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    id="firstName"
                    name="firstName"
                    label={translate('details.general.firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName && errors.firstName}
                    variant="outlined"
                    value={firstName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      if (value.trim().length) updateErrorState({ firstName: null });
                      updateState({ firstName: value });
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    id="middleName"
                    name="middleName"
                    label={translate('details.general.middleName')}
                    error={!!errors.middleName}
                    helperText={errors.middleName && errors.middleName}
                    variant="outlined"
                    value={middleName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      if (value.trim().length) updateErrorState({ middleName: null });
                      updateState({ middleName: value });
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    id="lastName"
                    name="lastName"
                    label={translate('details.general.lastName')}
                    error={!!errors.lastName}
                    helperText={errors.lastName && errors.lastName}
                    variant="outlined"
                    value={lastName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      if (value.trim().length) updateErrorState({ lastName: null });
                      updateState({ lastName: value });
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Autocomplete
                    options={valueSet?.asList() ?? []}
                    value={purpose}
                    onChange={(_, newValue) => {
                      updateState({ purpose: newValue })
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option?.display ?? ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      typeof option !== "string" &&
                      typeof value !== "string" &&
                      option.code === value.code
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={translate('details.general.type')}
                        placeholder={translate('details.general.type')}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </EditableCard>
            <ContactDetails 
              telecom={telecom ?? undefined}
              title={translate('details.general.contactTitle')}
              update={(data) => updateState({ telecom: data })}
              fullWidth={true}
              permissions={{
                isAllowedToAdd: true,
                isAllowedToEdit: true,
                isAllowedToDelete: true
              }}
              valueSetsIds={['patient-contact-use-email','patient-contact-use-phone']}
            />
            <EditableCard
              title={translate('details.general.addressDetails')}
              fullWidth={true}
              showEditButton={true}
              onEditButtonPress={() => updateState({ openAddAddress: true })}
            >
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="street"
                    name="street"
                    label={translate('details.general.street')}
                    variant="outlined"
                    value={address?.line?.[0] ?? ""}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="street1"
                    name="street1"
                    label={translate('details.general.street1')}
                    variant="outlined"
                    value={address?.line?.[1] ?? ""}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    id="city"
                    name="city"
                    label={translate('details.general.city')}
                    variant="outlined"
                    value={address?.city ?? ""}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    id="district"
                    name="district"
                    label={translate('details.general.district')}
                    variant="outlined"
                    value={address?.district ?? ""}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    id="state"
                    name="state"
                    label={translate('details.general.state')}
                    variant="outlined"
                    value={address?.state ?? ""}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    id="zipCode"
                    name="zipCode"
                    label={translate('details.general.zipCode')}
                    variant="outlined"
                    value={address?.postalCode ?? "" }
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </EditableCard>
          </Grid>
        </Box>
      </CustomModal>
      <CustomModal
        open={openAddAddress}
        title="Add Address"
        breadcrumbs={[""]}
        isLoading={false}
        onCancel={() => {}}
        showSaveButton={false}
        showCancelButton={false}
      >
        <AddAddressLoading
          isEditable
          externalAddress={address}
          handleClose={() => updateState({ openAddAddress: false })}
          handleSave={(data) => {
            updateState({ 
              address: data,
              openAddAddress: false
            })
          }}
          externalError={undefined}
        />
      </CustomModal>
    </>
  )
};

export default ContactModal;